import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Button } from '@lemon-io/ui-kit';
import Box from '@mui/material/Box';
import { useSnackbar } from 'notistack';

import { profileApi } from '@internal/api/profile';
import ConfirmModal from '@internal/components/common/confirm-modal/confirm-modal';
import Routes from '@internal/config/routes';

import { DescriptionLink } from './country-resolver.styled';
import { useCountryModalStorage } from './use-country-modal-storage';
import useLocationCountry from './use-location-country';

const SNOOZE_DURATION_MS = 30 * 24 * 60 * 60 * 1000;
const LOCATION_TAB_HREF = `${Routes.Profile}/location`;

interface CountryResolverProps {
  token?: string;
}

const CountryResolver = ({ token }: CountryResolverProps) => {
  const { profile } = profileApi.getProfile.useQuery(null, {
    enabled: Boolean(token),
  });

  const { country } = useLocationCountry();

  const { updateProfile, updateProfileLoading } =
    profileApi.updateProfile.useMutation();

  const storage = useCountryModalStorage();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const router = useRouter();

  const [showMismatchModal, setShowMismatchModal] = useState(false);

  useEffect(() => {
    if (
      !profile ||
      !country ||
      ['candidate', 'partner candidate'].includes(profile.role)
    ) {
      return;
    }

    if (storage.getValue('closed')) {
      return;
    }

    // Wait until all the content will be rendered,
    // so it will looks more smooth.
    setTimeout(() => setShowMismatchModal(country !== profile.country), 2000);
  }, [profile, country]);

  const snoozePopup = () => {
    storage.setValue('closed', true, new Date(Date.now() + SNOOZE_DURATION_MS));
  };

  const handleClose = () => {
    setShowMismatchModal(false);
    snoozePopup();
  };

  const handleUpdate = () => {
    if (!country) return;

    const isProjectApplicationPage =
      router.pathname === '/view-project/[dealId]';

    updateProfile(
      {
        country,
        apiKey: profile?.apiKey || '',
      },
      {
        onSuccess: () => {
          handleClose();

          enqueueSnackbar({
            variant: 'success',
            ...(!isProjectApplicationPage &&
              profile?.role === 'developer' && {
                action: key => (
                  <Link
                    href={LOCATION_TAB_HREF}
                    style={{
                      color: 'inherit',
                      textDecoration: 'none',
                      flexShrink: 0,
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="xsmall"
                      sx={{ flexShrink: 0 }}
                      onClick={() => closeSnackbar(key)}
                    >
                      Change time zone
                    </Button>
                  </Link>
                ),
              }),

            message: (
              <Box>
                Location changed. Please update timezone and working hours to
                keep receiving relevant offers.
              </Box>
            ),
          });
        },
      }
    );
  };

  return (
    <ConfirmModal
      open={showMismatchModal}
      isLoading={updateProfileLoading}
      title="Update your location"
      description={
        <>
          We noticed you&apos;re no longer in <b>{profile?.country}</b>. Would
          you like to update your location to <b>{country}</b>? You can always
          change your location manually{' '}
          <DescriptionLink href={LOCATION_TAB_HREF} onClick={handleClose}>
            here
          </DescriptionLink>
          .
        </>
      }
      confirmLabel={`Update to ${country}`}
      cancelLabel="Not now"
      loadingLabel="Updating..."
      width={480}
      dataTestId="country-resolver-modal"
      cancelTestId="country-resolver-not-now-button"
      confirmTestId="country-resolver-update-button"
      onClose={handleClose}
      onConfirm={handleUpdate}
    />
  );
};

export default CountryResolver;
